import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CHAT_READ_EVENT } from "@/lib/useChatUnread";

const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export const ClientChatPanel = ({ clientId, title = "Chat mit deinem Coach" }) => {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const load = () => api.get(`/client-chat/${clientId}`).then((r) => {
    setMsgs(r.data);
    window.dispatchEvent(new Event(CHAT_READ_EVENT));
  }).catch(() => setMsgs((m) => m || []));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client-chat/${clientId}`, { text });
      setMsgs((m) => [...(m || []), data]);
      setText("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 bg-white flex flex-col" data-testid="client-chat">
      <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5"><MessageCircle size={12} /> {title}</div>
      <div ref={boxRef} className="h-96 overflow-y-auto p-4 space-y-3 bg-slate-50/50" data-testid="client-chat-messages">
        {msgs === null && <div className="text-xs text-slate-400">Lädt…</div>}
        {msgs?.length === 0 && <div className="text-xs text-slate-400 text-center pt-10" data-testid="client-chat-empty">Noch keine Nachrichten.</div>}
        {(msgs || []).map((m) => {
          const mine = m.sender_user_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`client-chat-msg-${m.message_id}`}>
              <div className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap ${mine ? "brand-active text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                <div className={`text-[10px] mb-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>{m.sender_name} · {fmt(m.created_at)}</div>
                {m.text}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex border-t border-slate-200">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Nachricht schreiben…" className="flex-1 px-4 py-3 text-sm outline-none" data-testid="client-chat-input" />
        <button disabled={busy || !text.trim()} className="px-5 bg-slate-900 text-white text-sm disabled:opacity-40 flex items-center gap-1.5" data-testid="client-chat-send"><Send size={13} /> Senden</button>
      </form>
    </div>
  );
};
